import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Alert, 
  Container,
  Stack,
  IconButton,
  InputAdornment,
  Link
} from '@mui/material';
import { LoginOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { uiPT } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import Card from '../components/Card';

interface LoginFormData {
  username: string;
  password: string;
}

const schema = yup.object({
  username: yup.string().required('Usuário é obrigatório'),
  password: yup.string().required('Senha é obrigatória').min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Attempting login with username:', data.username);
      const response = await api.post('/auth/login', data);
      console.log('Login response:', response.data);
      
      // Use the login function from useAuth hook
      login(response.data.token, response.data.user);
      
      console.log('Login successful, user role:', response.data.user.role);
      
      // Navigate to single dashboard route
      console.log('Redirecting to dashboard');
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);
      console.error('Login error response:', err.response?.data);
      setError(err.response?.data?.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: (theme) => theme.palette.grey[100],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center">
            {/* Logo/Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: (theme) => theme.shadows[3],
              }}
            >
              <LoginOutlined sx={{ color: 'white', fontSize: 32 }} />
            </Box>

            {/* Title and Description */}
            <Stack spacing={1} alignItems="center" textAlign="center">
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {uiPT.login.title}
              </Typography>
            </Stack>

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
              <Stack spacing={3}>
                {error && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <Controller
                  name="username"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Usuário"
                      error={!!errors.username}
                      helperText={errors.username?.message}
                      disabled={loading}
                    />
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Senha"
                      type={showPassword ? 'text' : 'password'}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      disabled={loading}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }
                      }}
                    />
                  )}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    height: 48,
                    fontWeight: 700,
                    fontSize: '0.875rem',
                  }}
                >
                  {loading ? 'Entrando...' : uiPT.login.button}
                </Button>

                <Stack direction="row" justifyContent="center" spacing={1}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/register')}
                    sx={{
                      color: 'text.secondary',
                      textDecoration: 'none',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    Voltar ao início
                  </Link>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Card>
      </Container>
    </Box>
  );
} 